import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { signIn, signUp, signOut, getCurrentUser, confirmSignUp, resendSignUpCode } from 'aws-amplify/auth'

const useAuthStore = create(
  persist(
    (set) => ({
      isAuthenticated: false,
      user: null,
      error: null,
      isLoading: false,
      
      // Check authentication status on app load
      checkAuthStatus: async () => {
        try {
          set({ isLoading: true })
          const user = await getCurrentUser()
          set({ 
            isAuthenticated: true, 
            user: {
              username: user.username,
              userId: user.userId,
            }, 
            error: null,
            isLoading: false 
          })
          return true
        } catch (error) {
          set({ 
            isAuthenticated: false, 
            user: null, 
            error: null,
            isLoading: false 
          })
          return false
        }
      },
      
      // Sign in with username/email and password
      login: async (username, password) => {
        try {
          set({ isLoading: true, error: null })
          const { isSignedIn } = await signIn({ username, password })
          
          if (isSignedIn) {
            const user = await getCurrentUser()
            set({ 
              isAuthenticated: true, 
              user: {
                username: user.username,
                userId: user.userId,
              }, 
              error: null,
              isLoading: false 
            })
            return { success: true, user }
          } else {
            set({ isLoading: false })
            return { success: false, error: 'Sign in incomplete' }
          }
        } catch (error) {
          let errorMessage = 'An error occurred during sign in'
          
          if (error.name === 'NotAuthorizedException') {
            errorMessage = 'Incorrect username or password'
          } else if (error.name === 'UserNotConfirmedException') {
            errorMessage = 'User is not confirmed. Please check your email for verification code.'
          } else if (error.name === 'PasswordResetRequiredException') {
            errorMessage = 'Password reset required'
          } else if (error.name === 'UserNotFoundException') {
            errorMessage = 'User does not exist'
          } else {
            errorMessage = error.message || errorMessage
          }
          
          set({ 
            isAuthenticated: false, 
            user: null, 
            error: errorMessage,
            isLoading: false 
          })
          return { success: false, error: errorMessage }
        }
      },
      
      // Sign up new user
      signUp: async (username, password, email, givenName, familyName) => {
        try {
          set({ isLoading: true, error: null })
          const { userId, isSignUpComplete, nextStep } = await signUp({
            username,
            password,
            options: {
              userAttributes: {
                email: email,
                'given_name': givenName,
                'family_name': familyName,
              },
            },
          })
          
          set({ isLoading: false })
          
          return { 
            success: true, 
            userId,
            isSignUpComplete,
            nextStep,
            requiresConfirmation: nextStep.signUpStep === 'CONFIRM_SIGN_UP'
          }
        } catch (error) {
          let errorMessage = 'An error occurred during sign up'
          
          if (error.name === 'UsernameExistsException') {
            errorMessage = 'Username already exists'
          } else if (error.name === 'InvalidPasswordException') {
            errorMessage = 'Password does not meet requirements'
          } else if (error.name === 'InvalidParameterException') {
            // Check if error is about missing name attributes
            if (error.message && (error.message.includes('given_name') || error.message.includes('family_name'))) {
              errorMessage = 'First name and last name are required'
            } else {
              errorMessage = error.message || 'Invalid email or username format'
            }
          } else {
            errorMessage = error.message || errorMessage
          }
          
          set({ 
            error: errorMessage,
            isLoading: false 
          })
          return { success: false, error: errorMessage }
        }
      },
      
      // Confirm sign up with verification code
      confirmSignUp: async (username, confirmationCode) => {
        try {
          set({ isLoading: true, error: null })
          const { isSignUpComplete, nextStep } = await confirmSignUp({
            username,
            confirmationCode,
          })
          
          set({ isLoading: false })
          return { 
            success: true, 
            isSignUpComplete,
            nextStep 
          }
        } catch (error) {
          let errorMessage = 'An error occurred during confirmation'
          
          if (error.name === 'CodeMismatchException') {
            errorMessage = 'Invalid verification code'
          } else if (error.name === 'ExpiredCodeException') {
            errorMessage = 'Verification code has expired'
          } else {
            errorMessage = error.message || errorMessage
          }
          
          set({ 
            error: errorMessage,
            isLoading: false 
          })
          return { success: false, error: errorMessage }
        }
      },
      
      // Resend verification code
      resendConfirmationCode: async (username) => {
        try {
          set({ isLoading: true, error: null })
          await resendSignUpCode({ username })
          set({ isLoading: false })
          return { success: true }
        } catch (error) {
          const errorMessage = error.message || 'Failed to resend verification code'
          set({ 
            error: errorMessage,
            isLoading: false 
          })
          return { success: false, error: errorMessage }
        }
      },
      
      // Sign out
      logout: async () => {
        try {
          set({ isLoading: true })
          await signOut()
          set({ 
            isAuthenticated: false, 
            user: null, 
            error: null,
            isLoading: false 
          })
        } catch (error) {
          console.error('Error signing out:', error)
          // Still clear local state even if sign out fails
          set({ 
            isAuthenticated: false, 
            user: null, 
            error: null,
            isLoading: false 
          })
        }
      },
      
      // Clear error
      clearError: () => set({ error: null }),
    }),
    {
      name: 'soccer-play-auth',
      // Don't persist authentication state - always verify with Cognito
      // This prevents localStorage tampering from bypassing authentication
      partialize: (state) => {
        // Don't persist isAuthenticated - always verify with Cognito on each page load
        return {}
      },
    }
  )
)

export default useAuthStore

